import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { logout } from '../lib/phpAuth'
import { getSettings } from '../lib/apiPhpAdapter'
import { parseModuleDevFlags, type ModuleDevFlags } from '../lib/moduleDevAccess'
import {
  buildCabinetModuleLinks,
  formatAssignedRole,
  type CabinetLink,
} from '../lib/intranetHubLinks'
import { useIntranetAccess } from '../hooks/useIntranetAccess'
import { useAlert } from '../contexts/AlertContext'
import { CabinetMonthGrid } from '../components/cabinet/CabinetMonthGrid'
import { CabinetTodayBirthdays } from '../components/cabinet/CabinetTodayBirthdays'
import { CabinetLeaveBalances } from '../components/cabinet/CabinetLeaveBalances'
import WeeklyNews from '../components/WeeklyNews'
import type { CalendarEvent } from '../lib/icsCalendar'
import { formatEventTimeRange } from '../lib/icsCalendar'
import {
  loadCabinetActivityRowsForUser,
  loadSchoolEventsForMonth,
  resolveSchoolIcsUrl,
  buildMonthStats,
  activityModuleLabel,
  activityStatusFromRow,
  monthOverlapsRange,
  type CabinetActivityRow,
  type ActivityKind,
} from '../lib/myCabinetActivity'
import { MONTH_NAMES_CZ } from '../lib/calendarMonthUtils'
import { withCabinetReturn } from '../lib/cabinetReturn'
import './MyCabinet.css'

type DayModal = {
  kind: 'school'
  day: number
  events: CalendarEvent[]
}

type PeriodFilter = 'all' | 'calendar_month' | '90d'

/** Filtrování podle účasti v záznamu (Můj kabinet). */
type InvolvementFilter = 'all' | 'author' | 'process' | 'foreign_only'

type MobileTab = 'prehled' | 'kalendare' | 'aplikace'

function rowMatchesInvolvement(row: CabinetActivityRow, f: InvolvementFilter): boolean {
  if (f === 'all') return true
  const a = row.cabinetIsAuthor === true
  const p = row.cabinetIsProcessRole === true
  if (f === 'author') return a
  if (f === 'process') return p
  if (f === 'foreign_only') return p && !a
  return true
}

const ALL_SYSTEMS: Array<'all' | ActivityKind> = [
  'all',
  'ticket',
  'property_move',
  'purchase',
  'travel',
  'dovolena',
  'fksp',
]

function systemOptionLabel(v: (typeof ALL_SYSTEMS)[number]): string {
  if (v === 'all') return 'Všechny systémy'
  return activityModuleLabel(v)
}

function rowMatchesSystem(row: CabinetActivityRow, system: (typeof ALL_SYSTEMS)[number]): boolean {
  if (system === 'all') return true
  return row.kind === system
}

function rowOverlapsLastDays(row: CabinetActivityRow, days: number): boolean {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - days)
  const startY = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
  const endY = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
  return row.rangeStart <= endY && row.rangeEnd >= startY
}

export default function MyCabinet() {
  const navigate = useNavigate()
  const { user, authLoading, accessGranted, loading } = useIntranetAccess()
  const { showAlert } = useAlert()
  const [moduleDevFlags, setModuleDevFlags] = useState<ModuleDevFlags | null>(null)
  const [viewMonth, setViewMonth] = useState(() => new Date())
  const [schoolEvents, setSchoolEvents] = useState<CalendarEvent[]>([])
  const [schoolErr, setSchoolErr] = useState<string | null>(null)
  const [calLoading, setCalLoading] = useState(true)
  const [tableRows, setTableRows] = useState<CabinetActivityRow[]>([])
  const [tableLoading, setTableLoading] = useState(true)
  const [tableError, setTableError] = useState<string | null>(null)
  const [dayModal, setDayModal] = useState<DayModal | null>(null)

  const [filterSystem, setFilterSystem] = useState<(typeof ALL_SYSTEMS)[number]>('all')
  const [filterSearch, setFilterSearch] = useState('')
  const [filterPeriod, setFilterPeriod] = useState<PeriodFilter>('all')
  const [filterInvolvement, setFilterInvolvement] = useState<InvolvementFilter>('all')
  const [mobileTab, setMobileTab] = useState<MobileTab>('prehled')

  const y = viewMonth.getFullYear()
  const m = viewMonth.getMonth()

  useEffect(() => {
    if (!accessGranted || !user) return
    let cancelled = false
    getSettings()
      .then((s) => {
        if (!cancelled) setModuleDevFlags(parseModuleDevFlags(s))
      })
      .catch(() => {
        if (!cancelled) {
          setModuleDevFlags({ fksp: true, dovolena: true, travel_orders: true })
        }
      })
    return () => {
      cancelled = true
    }
  }, [accessGranted, user])

  const loadCalendars = useCallback(async () => {
    if (!user || accessGranted !== true) return
    setCalLoading(true)
    setSchoolErr(null)
    try {
      const icsUrl = await resolveSchoolIcsUrl()
      const schoolRes = await loadSchoolEventsForMonth(y, m, icsUrl)
      setSchoolEvents(schoolRes.events)
      setSchoolErr(schoolRes.error)
    } catch (e) {
      console.error(e)
      setSchoolErr('Nepodařilo se načíst kalendář')
    } finally {
      setCalLoading(false)
    }
  }, [user, accessGranted, y, m])

  const loadTable = useCallback(async () => {
    if (!user || accessGranted !== true) return
    setTableLoading(true)
    setTableError(null)
    try {
      const rows = await loadCabinetActivityRowsForUser(user)
      setTableRows(rows)
    } catch (e) {
      console.error(e)
      setTableRows([])
      setTableError('Nepodařilo se načíst přehled žádostí.')
    } finally {
      setTableLoading(false)
    }
  }, [user, accessGranted])

  useEffect(() => {
    loadCalendars()
  }, [loadCalendars])

  useEffect(() => {
    loadTable()
  }, [loadTable])

  const monthStats = useMemo(() => buildMonthStats(tableRows, y, m), [tableRows, y, m])

  const filteredTableRows = useMemo(() => {
    const q = filterSearch.trim().toLowerCase()
    return tableRows.filter((row) => {
      if (!rowMatchesSystem(row, filterSystem)) return false
      if (!rowMatchesInvolvement(row, filterInvolvement)) return false
      if (filterPeriod === 'calendar_month') {
        if (!monthOverlapsRange(y, m, row.rangeStart, row.rangeEnd)) return false
      } else if (filterPeriod === '90d') {
        if (!rowOverlapsLastDays(row, 90)) return false
      }
      if (q) {
        const blob = `${activityModuleLabel(row.kind)} ${row.title} ${row.subtitle || ''} ${row.cabinetInvolvementHint || ''}`.toLowerCase()
        if (!blob.includes(q)) return false
      }
      return true
    })
  }, [tableRows, filterSystem, filterInvolvement, filterSearch, filterPeriod, y, m])

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/', { replace: true })
    } catch {
      navigate('/', { replace: true })
    }
  }

  const modules = useMemo(
    () => (user ? buildCabinetModuleLinks(user, moduleDevFlags) : []),
    [user, moduleDevFlags]
  )

  const onModuleClick = (link: CabinetLink) => {
    if (link.disabled && link.blockedMessage) {
      showAlert(link.blockedMessage, 'warning')
      return
    }
    if (link.disabled) return
    if (link.to) navigate(link.to)
  }

  const initials = useMemo(() => {
    const n = user?.name || user?.email || '?'
    const p = n.trim().split(/\s+/)
    if (p.length >= 2) return (p[0][0] + p[1][0]).toUpperCase()
    return n.slice(0, 2).toUpperCase()
  }, [user])

  if (loading || accessGranted === null || authLoading) {
    return (
      <div className="my-cabinet" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="my-cabinet__spinner" aria-hidden />
      </div>
    )
  }

  if (accessGranted === false || !user) {
    return (
      <div className="my-cabinet" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="my-cabinet__spinner" />
      </div>
    )
  }

  return (
    <div className="my-cabinet">
      <div className="my-cabinet__shell">
        <div className="my-cabinet__hero">
          <div className="my-cabinet__hero-main">
            <button
              type="button"
              className="my-cabinet__nav-btn"
              onClick={() => navigate('/intranet')}
            >
              ← Intranet
            </button>
            <div className="my-cabinet__avatar" aria-hidden>
              {initials}
            </div>
            <div className="my-cabinet__hero-text">
              <p className="my-cabinet__eyebrow">Můj kabinet</p>
              <h1>Vítejte, {user.name || 'uživateli'}</h1>
              <p>Kalendáře · přehled žádostí · aplikace</p>
            </div>
          </div>
          <div className="my-cabinet__hero-meta">
            <span className="my-cabinet__chip">{user.email}</span>
            <span className="my-cabinet__chip">Role: {formatAssignedRole(user)}</span>
            <button type="button" className="my-cabinet__logout" onClick={handleLogout}>
              Odhlásit se
            </button>
          </div>
        </div>

        <div className="my-cabinet__glass">
          <CabinetLeaveBalances />
          <div className="my-cabinet__month-bar">
            <h2 className="my-cabinet__month-title">
              <span className="my-cabinet__month-title-full">Kalendáře: </span>
              {MONTH_NAMES_CZ[m]} {y}
            </h2>
            <CabinetTodayBirthdays user={user} />
            <div className="my-cabinet__month-nav">
              <button
                type="button"
                className="my-cabinet__icon-btn"
                aria-label="Předchozí měsíc"
                onClick={() => setViewMonth(new Date(y, m - 1, 1))}
              >
                ‹
              </button>
              <button type="button" className="my-cabinet__today-btn" onClick={() => setViewMonth(new Date())}>
                Dnes
              </button>
              <button
                type="button"
                className="my-cabinet__icon-btn"
                aria-label="Další měsíc"
                onClick={() => setViewMonth(new Date(y, m + 1, 1))}
              >
                ›
              </button>
            </div>
          </div>

          <nav className="my-cabinet__mobile-tabs" aria-label="Sekce kabinetu">
            <button
              type="button"
              className={mobileTab === 'prehled' ? 'is-active' : undefined}
              onClick={() => setMobileTab('prehled')}
            >
              Přehled
              {!tableLoading && filteredTableRows.length > 0 ? (
                <span className="my-cabinet__mobile-tab-count">{filteredTableRows.length}</span>
              ) : null}
            </button>
            <button
              type="button"
              className={mobileTab === 'kalendare' ? 'is-active' : undefined}
              onClick={() => setMobileTab('kalendare')}
            >
              Kalendáře
            </button>
            <button
              type="button"
              className={mobileTab === 'aplikace' ? 'is-active' : undefined}
              onClick={() => setMobileTab('aplikace')}
            >
              Aplikace
            </button>
          </nav>

          <div className="my-cabinet__layout3" data-mobile-tab={mobileTab}>
            {/* Levý sloupec — kalendáře */}
            <aside
              className={`my-cabinet__col my-cabinet__col--left${mobileTab === 'kalendare' ? ' is-mobile-active' : ''}`}
            >
              <div className="my-cabinet__cal-stack">
                <section className="my-cabinet__cal-block my-cabinet__cal-block--news">
                  <WeeklyNews showSchoolCalendar={false} className="my-cabinet__weekly-news" />
                </section>
                <section className="my-cabinet__cal-block">
                  <div className="my-cabinet__cal-block-head">
                    <h3>Školní akce</h3>
                    <button type="button" className="my-cabinet__text-link" onClick={() => navigate('/calendar')}>
                      Plný kalendář →
                    </button>
                  </div>
                  {schoolErr && <p className="my-cabinet__cal-err">{schoolErr}</p>}
                  <CabinetMonthGrid
                    year={y}
                    month={m}
                    events={schoolEvents}
                    loading={calLoading}
                    accent="teal"
                    emptyHint="Žádné události."
                    onDayClick={(day, evs) => setDayModal({ kind: 'school', day, events: evs })}
                  />
                </section>
              </div>
            </aside>

            {/* Střed — tabulka */}
            <main
              className={`my-cabinet__col my-cabinet__col--center${mobileTab === 'prehled' ? ' is-mobile-active' : ''}`}
            >
              <div className="my-cabinet__center-head">
                <div className="my-cabinet__center-title-row">
                  <h2>Přehled žádostí a záznamů</h2>
                  <span className="my-cabinet__center-count">
                    {tableLoading ? '…' : `${filteredTableRows.length} / ${tableRows.length}`}
                  </span>
                </div>
                {tableError && <p className="my-cabinet__cal-err">{tableError}</p>}
                <div className="my-cabinet__filters">
                  <label className="my-cabinet__filter">
                    <span>Systém</span>
                    <select value={filterSystem} onChange={(e) => setFilterSystem(e.target.value as any)}>
                      {ALL_SYSTEMS.map((opt) => (
                        <option key={opt} value={opt}>
                          {systemOptionLabel(opt)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="my-cabinet__filter">
                    <span>Období</span>
                    <select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value as PeriodFilter)}>
                      <option value="all">Vše</option>
                      <option value="calendar_month">Měsíc ({MONTH_NAMES_CZ[m]})</option>
                      <option value="90d">90 dní</option>
                    </select>
                  </label>
                  <label className="my-cabinet__filter">
                    <span>Účast</span>
                    <select
                      value={filterInvolvement}
                      onChange={(e) => setFilterInvolvement(e.target.value as InvolvementFilter)}
                    >
                      <option value="all">Všechny mé</option>
                      <option value="author">Mnou zahájené</option>
                      <option value="process">Čeká na mou akci</option>
                      <option value="foreign_only">Jen čekající na mě</option>
                    </select>
                  </label>
                  <label className="my-cabinet__filter my-cabinet__filter--grow">
                    <span>Hledat</span>
                    <input
                      type="search"
                      placeholder="Hledat…"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                    />
                  </label>
                  <button type="button" className="my-cabinet__refresh-btn" onClick={() => loadTable()} disabled={tableLoading}>
                    {tableLoading ? '…' : 'Obnovit'}
                  </button>
                </div>
                <div className="my-cabinet__mini-stats">
                  <span className="my-cabinet__mini-stat">
                    <strong>{MONTH_NAMES_CZ[m]}</strong>:
                  </span>
                  {monthStats.map((s) => (
                    <span key={s.key} className="my-cabinet__mini-stat">
                      <strong>{s.count}</strong> {s.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="my-cabinet__table-wrap">
                <table className="my-cabinet__table">
                  <thead>
                    <tr>
                      <th>Systém</th>
                      <th>Záznam</th>
                      <th>Období</th>
                      <th>Stav</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading && (
                      <tr>
                        <td colSpan={4} className="my-cabinet__table-empty">
                          Načítání…
                        </td>
                      </tr>
                    )}
                    {!tableLoading && filteredTableRows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="my-cabinet__table-empty">
                          {tableError
                            ? tableError
                            : tableRows.length === 0
                              ? 'Žádné položky — jen vaše nedokončené žádosti, nebo procesy čekající na vaši akci.'
                              : 'Žádné záznamy neodpovídají filtru.'}
                        </td>
                      </tr>
                    )}
                    {!tableLoading &&
                      filteredTableRows.map((row) => (
                        <tr
                          key={row.id}
                          className="my-cabinet__table-row--click"
                          tabIndex={0}
                          role="link"
                          onClick={() => navigate(withCabinetReturn(row.path))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              navigate(withCabinetReturn(row.path))
                            }
                          }}
                        >
                          <td data-label="Systém">
                            <span className="my-cabinet__sys-pill">{activityModuleLabel(row.kind)}</span>
                          </td>
                          <td data-label="Záznam">
                            <div className="my-cabinet__cell-title">{row.title}</div>
                            {row.subtitle && <div className="my-cabinet__cell-sub">{row.subtitle}</div>}
                          </td>
                          <td className="my-cabinet__cell-dates" data-label="Období">
                            {row.rangeStart === row.rangeEnd ? row.rangeStart : `${row.rangeStart} → ${row.rangeEnd}`}
                          </td>
                          <td data-label="Stav">{activityStatusFromRow(row)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </main>

            {/* Pravý sloupec — tlačítka */}
            <aside
              className={`my-cabinet__col my-cabinet__col--right${mobileTab === 'aplikace' ? ' is-mobile-active' : ''}`}
            >
              <h2 className="my-cabinet__apps-title">Aplikace</h2>
              <div className="my-cabinet__btn-col">
                {modules.map((link) => {
                  const cls = `my-cabinet__app-btn my-cabinet__app-btn--block${link.devMode && !link.disabled ? ' my-cabinet__app-btn--dev' : ''}`
                  const inner = (
                    <>
                      <span className="emoji" aria-hidden>
                        {link.icon}
                      </span>
                      <span className="my-cabinet__app-btn-label">{link.title}</span>
                      {link.devMode && !link.disabled && (
                        <span className="my-cabinet__app-dev">VÝVOJ</span>
                      )}
                    </>
                  )
                  if (link.href && !link.disabled) {
                    return (
                      <a
                        key={link.id}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cls}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {inner}
                      </a>
                    )
                  }
                  return (
                    <button
                      key={link.id}
                      type="button"
                      className={cls}
                      disabled={!!link.disabled}
                      onClick={() => onModuleClick(link)}
                    >
                      {inner}
                    </button>
                  )
                })}
              </div>
            </aside>
          </div>
        </div>
      </div>

      {dayModal && (
        <div
          className="my-cabinet__modal-back"
          role="presentation"
          onClick={() => setDayModal(null)}
          onKeyDown={(e) => e.key === 'Escape' && setDayModal(null)}
        >
          <div
            className="my-cabinet__modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <header>
              <h4>
                Školní akce — {dayModal.day}. {MONTH_NAMES_CZ[m]} {y}
              </h4>
              <button type="button" className="my-cabinet__modal-close" aria-label="Zavřít" onClick={() => setDayModal(null)}>
                ×
              </button>
            </header>
            <ul>
              {dayModal.events.length === 0 && <li className="li-sub">Žádné položky v tento den.</li>}
              {dayModal.events.map((ev) => {
                return (
                  <li key={ev.id}>
                    <div className="li-title">{ev.subject}</div>
                    <div className="li-sub">{formatEventTimeRange(ev)}</div>
                    {ev.location?.displayName && <div className="li-sub">📍 {ev.location.displayName}</div>}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
